import { useState } from "react";
import { LogOut, Download, Sparkles, Trash2, PlayCircle, Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";
import { API, client } from "@/lib/api";
import ProductTour from "@/components/ProductTour";
import IntegrationsCard from "@/components/IntegrationsCard";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ADMIN_EMAILS = new Set(["pradeepmunjal029@gmail.com", "creator@pintaflow.com"]);
const INK_55 = "var(--ink-55)";

export default function Settings() {
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // "seed" | "clear" | "wipe" | null
  const [msg, setMsg] = useState("");
  const [showTour, setShowTour] = useState(false);
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeText, setWipeText] = useState("");
  const isAdmin = user?.email && ADMIN_EMAILS.has(user.email);

  const exportCsv = async (entity) => {
    const token = localStorage.getItem("session_token");
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const res = await fetch(`${API}/export/csv?entity=${entity}`, { credentials: "include", headers });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `pintaflow_${entity}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const seedDemo = async () => {
    setBusy("seed");
    setMsg("");
    try {
      await client.post("/demo/seed", {});
      setMsg("Demo workspace loaded — 7 ideas, 4 tasks, 3 swipes, sample analytics, streak. Open your Dashboard.");
    } catch (e) {
      setMsg("Could not seed demo. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const clearDemo = async () => {
    if (!window.confirm("Remove all demo-tagged ideas, swipes, tasks, metrics & demo goal? Your own data is safe.")) return;
    setBusy("clear");
    setMsg("");
    try {
      await client.post("/demo/clear", {});
      setMsg("Demo data cleared. Your own ideas, tasks and goals are untouched.");
    } finally {
      setBusy(null);
    }
  };

  const wipeAllPosts = async () => {
    setBusy("wipe");
    try {
      const r = await client.delete("/metrics");
      toast.success(`${r.data.deleted} logged posts permanently deleted`);
      setWipeOpen(false);
      setWipeText("");
    } catch (e) {
      toast.error("Could not delete posts. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const replayTour = async () => {
    await client.patch("/auth/tour", { seen: false });
    refresh?.();
    setShowTour(true);
  };

  const replayCoach = () => {
    window.dispatchEvent(new CustomEvent("pintaflow:coach-replay"));
    navigate("/dashboard");
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> The account</div>
      <h1 className="font-heading mt-4 mb-10 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
        Settings
      </h1>

      <div className="card mb-4 flex items-center gap-4">
        {user?.picture ? (
          <img src={user.picture} alt="" className="w-14 h-14 rounded-full" />
        ) : (
          <div className="w-14 h-14 rounded-full" style={{ background: "var(--surface-2)" }} />
        )}
        <div className="flex-1">
          <div className="font-heading text-lg" style={{ fontVariationSettings: "'opsz' 32, 'wght' 500" }} data-testid="settings-name">{user?.name}</div>
          <div className="text-sm" style={{ color: INK_55 }} data-testid="settings-email">{user?.email}</div>
        </div>
      </div>

      <div className="card mb-4">
        <div className="eyebrow mb-2">Learn the studio</div>
        <div className="text-sm mb-4" style={{ color: "var(--ink-72)" }}>
          New here? Take the 60-second walkthrough or fill your workspace with a sample creator setup to feel the app first.
        </div>
        <div className="flex flex-wrap gap-2">
          <button onClick={replayCoach} className="btn-primary" data-testid="settings-replay-coach">
            <PlayCircle size={13} /> Replay first-run coach
          </button>
          <button onClick={replayTour} className="btn-secondary" data-testid="settings-replay-tour">
            <PlayCircle size={13} /> Replay 60-sec walkthrough
          </button>
          <button onClick={seedDemo} disabled={busy !== null} className="btn-secondary disabled:opacity-50" data-testid="settings-load-demo">
            {busy === "seed" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
            {busy === "seed" ? "Loading…" : "Load demo workspace"}
          </button>
          <button onClick={clearDemo} disabled={busy !== null} className="btn-ghost" data-testid="settings-clear-demo" style={{ color: "#c97a6a" }}>
            <Trash2 size={13} /> Clear demo data
          </button>
        </div>
        {msg && (
          <div className="text-xs mt-4 p-3 rounded-lg" style={{ background: "rgba(216,168,87,0.08)", border: "1px solid rgba(216,168,87,0.30)", color: "var(--ink)" }} data-testid="settings-demo-msg">
            {msg}
          </div>
        )}
      </div>

      <div className="mb-4">
        <IntegrationsCard />
      </div>

      <div className="card mb-4">
        <div className="eyebrow mb-4">Export your data (CSV)</div>
        <div className="flex flex-wrap gap-2">
          <button onClick={() => exportCsv("ideas")} className="btn-secondary" data-testid="export-ideas">
            <Download size={13} /> Ideas
          </button>
          <button onClick={() => exportCsv("tasks")} className="btn-secondary" data-testid="export-tasks">
            <Download size={13} /> Tasks
          </button>
          <button onClick={() => exportCsv("swipes")} className="btn-secondary" data-testid="export-swipes">
            <Download size={13} /> Swipes
          </button>
        </div>
        <div className="text-xs mt-4" style={{ color: INK_55 }}>JSON export & API access — coming in Pro.</div>
      </div>

      <div className="card mb-4" style={{ border: "1px solid rgba(201,122,106,0.35)" }}>
        <div className="eyebrow mb-2 flex items-center gap-2" style={{ color: "#c97a6a" }}>
          <AlertTriangle size={12} /> Danger zone
        </div>
        <div className="text-sm mb-4" style={{ color: "var(--ink-72)" }}>
          Permanently delete <strong>all your logged posts &amp; analytics data</strong> — manual entries, demo data and YouTube-synced videos. This cannot be undone.
        </div>
        <button
          onClick={() => { setWipeText(""); setWipeOpen(true); }}
          disabled={busy !== null}
          className="btn-secondary disabled:opacity-50"
          style={{ color: "#c97a6a", borderColor: "rgba(201,122,106,0.45)" }}
          data-testid="settings-wipe-posts-btn"
        >
          <Trash2 size={13} /> Delete all logged posts
        </button>
      </div>

      <AlertDialog open={wipeOpen} onOpenChange={(o) => { setWipeOpen(o); if (!o) setWipeText(""); }}>
        <AlertDialogContent data-testid="wipe-confirm-dialog" style={{ background: "var(--surface)", border: "1px solid rgba(201,122,106,0.4)" }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2" style={{ color: "#c97a6a" }}>
              <AlertTriangle size={16} /> Final warning
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: "var(--ink-72)" }}>
              Every logged post, metric and YouTube-synced video in your account will be <strong>permanently deleted</strong>. Ideas, tasks and swipes are safe. Type <strong>DELETE</strong> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <input
            value={wipeText}
            onChange={(e) => setWipeText(e.target.value)}
            placeholder="Type DELETE to confirm"
            className="w-full rounded-lg px-3 py-2 text-sm outline-none"
            style={{ background: "var(--bg)", border: "1px solid var(--ink-22)", color: "var(--ink)" }}
            data-testid="wipe-confirm-input"
          />
          <AlertDialogFooter>
            <button onClick={() => setWipeOpen(false)} className="btn-secondary" data-testid="wipe-cancel-btn">
              Cancel
            </button>
            <button
              onClick={wipeAllPosts}
              disabled={wipeText !== "DELETE" || busy === "wipe"}
              className="btn-primary disabled:opacity-40"
              style={{ background: "#c97a6a", borderColor: "#c97a6a" }}
              data-testid="wipe-confirm-btn"
            >
              {busy === "wipe" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
              Delete everything
            </button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="card">
        <div className="eyebrow mb-4">Session</div>
        <div className="flex flex-wrap gap-2">
          {isAdmin && (
            <button onClick={() => navigate("/admin")} className="btn-secondary" data-testid="settings-admin-link">
              <ShieldCheck size={13} /> Open admin
            </button>
          )}
          <button onClick={logout} className="btn-secondary" data-testid="settings-logout-btn" style={{ color: "#c97a6a" }}>
            <LogOut size={13} /> Sign out
          </button>
        </div>
      </div>

      {showTour && (
        <ProductTour onClose={() => { setShowTour(false); refresh?.(); }} />
      )}
    </div>
  );
}
